import { ErrorCorrectionConfidenceLevel } from '@global/api/error-correction/type'
import { IDatasetQueryResponse, IDatasetQueryResponseRow } from '@global/api/types'
import { User } from '@global/types'

interface FieldData {
  value: any
  comments?: CellComment[]
}

function makeDatasetValue(
  cvid: string,
  fieldId: string,
  fieldValue: any,
  comments?: CellComment[],
): DatasetValue {
  return {
    cell_value: fieldValue,
    field_id: fieldId,
    cvid,
    comments,
  }
}

function makeDatasetRow(queryRow: IDatasetQueryResponseRow) {
  const { cvid, data } = queryRow

  const dataset_values = Object.entries(data).map(([fieldId, untypedFieldData]) => {
    const fieldData = untypedFieldData as FieldData
    const datasetValue = makeDatasetValue(cvid, fieldId, fieldData.value, fieldData.comments)

    const datasetRow = {
      ...datasetValue,
    }

    return datasetRow
  })

  const datasetValuesWithCvid = {
    cvid,
    dataset_values,
  }

  return datasetValuesWithCvid
}

interface IDatasetFromQueryData {
  id: number
  uuid?: string
  dataset_rows: any[]
  commitRef?: string
  link_validation_results?: string[]
}

export function makeDatasetFromQueryData(queryData: IDatasetQueryResponse): IDatasetFromQueryData {
  const datasetRows = queryData.rows?.map(queryRow => makeDatasetRow(queryRow))
  return {
    id: queryData.datasetId,
    dataset_rows: datasetRows,
    ...(queryData.commitRef && { commitRef: queryData.commitRef }),
  }
}

export interface CellComment {
  commentId: number
  message: string
  user: User
}

export type CellComments = Record<string, Record<string, CellComment>>

export interface DatasetValue {
  cell_value: string | string[] | null
  cvid: string
  field_id: string
  comments?: CellComment[]
  errorCorrection?: {
    previousValue: string
    modification: string
    confidence: ErrorCorrectionConfidenceLevel
  }
}

/**
 * Creates a comments object for the DataContainer constructor.
 * The comments object is used in data-container.js to append comments to the cell's metadata.
 */
export function getCellComments(datasetValues: DatasetValue[]): CellComments {
  const datasetValuesWithComments = datasetValues
    .filter(dsValue => dsValue?.comments?.length)
    .flatMap(dsValue => ({
      cvId: dsValue.cvid,
      fieldId: dsValue.field_id,
      ...dsValue.comments![0]!,
    }))

  const cellComments: CellComments = {}

  datasetValuesWithComments.map(dsValue => {
    const { cvId, fieldId, commentId, message, user } = dsValue

    cellComments[cvId] = {
      ...cellComments[cvId],
      [fieldId]: {
        commentId,
        message,
        user,
      },
    }
  })

  return cellComments
}
